import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'

interface AISpend {
  since: string
  total: number
  byTenant: {
    tenantId: string; probeCents: number; agentCents: number; total: number
    tenant: { id: string; name: string; plan: string; internal: boolean } | null
  }[]
  byEngine: { engineId: string; calls: number; costCents: number }[]
}

export function AdminAISpendPage() {
  const t = useT()
  const spend = useQuery({
    queryKey: ['admin-ai-spend'],
    queryFn: () => apiClient.get<AISpend>('/admin/ai-spend'),
    refetchInterval: 60_000,
  })

  if (spend.isLoading) return <p>{t('common.loading')}</p>
  if (spend.isError || !spend.data) return <p>{t('common.error')}</p>

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-1">{t('admin.aispend.title')}</h1>
      <p className="text-xs opacity-70 mb-6">{t('admin.aispend.since', { date: new Date(spend.data.since).toLocaleDateString() })}</p>

      <div className="mb-6 p-4 rounded border" style={{ borderColor: 'var(--color-border)' }}>
        <div className="text-xs opacity-70">{t('admin.aispend.platform_total')}</div>
        <div className="text-3xl font-bold">${(spend.data.total / 100).toFixed(2)}</div>
      </div>

      <section className="mb-6">
        <h2 className="text-sm font-medium opacity-80 mb-2">{t('admin.aispend.by_tenant')}</h2>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left opacity-70">
              <th className="py-1 pr-4">tenant</th>
              <th className="py-1 pr-4">probes</th>
              <th className="py-1 pr-4">agents</th>
              <th className="py-1 pr-4">total</th>
            </tr>
          </thead>
          <tbody>
            {spend.data.byTenant.map((row) => (
              <tr key={row.tenantId} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                <td className="py-1 pr-4">
                  {row.tenant ? (
                    <Link to={`/admin/tenants/${row.tenant.id}`} className="hover:underline">{row.tenant.name}</Link>
                  ) : (
                    <span className="font-mono text-xs">{row.tenantId.slice(0, 8)}…</span>
                  )}
                  {row.tenant?.internal && <span className="text-xs opacity-60 ml-1">(internal)</span>}
                </td>
                <td className="py-1 pr-4">${(row.probeCents / 100).toFixed(2)}</td>
                <td className="py-1 pr-4">${(row.agentCents / 100).toFixed(2)}</td>
                <td className="py-1 pr-4 font-medium">${(row.total / 100).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2 className="text-sm font-medium opacity-80 mb-2">{t('admin.aispend.by_engine')}</h2>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left opacity-70">
              <th className="py-1 pr-4">engine</th>
              <th className="py-1 pr-4">calls</th>
              <th className="py-1 pr-4">cost</th>
              <th className="py-1 pr-4">avg/call</th>
            </tr>
          </thead>
          <tbody>
            {spend.data.byEngine.map((row) => (
              <tr key={row.engineId} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                <td className="py-1 pr-4">{row.engineId}</td>
                <td className="py-1 pr-4">{row.calls.toLocaleString()}</td>
                <td className="py-1 pr-4">${(row.costCents / 100).toFixed(2)}</td>
                <td className="py-1 pr-4">${(row.calls > 0 ? row.costCents / row.calls / 100 : 0).toFixed(4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  )
}
